import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LogIn } from 'lucide-react';
import FormInput from '../../components/FormInput';
import LoginOtpStep from '../../components/LoginOtpStep';
import BookmarkHint from '../../components/BookmarkHint';
import { useAuth } from '../../hooks/useAuth';
import { errorMessage } from '../../api/client';
import { BRAND_NAME } from '../../utils/brand';
import { isTrustedAbsoluteUrl } from '../../utils/redirect';

// Login page for ADMIN / STAFF / CLINIC (VENDOR) accounts.
// Email + password only — no access code required.
function PortalLogin() {
  const { signInPortal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const from = new URLSearchParams(location.search).get('from') || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [otpChallenge, setOtpChallenge] = useState(null);

  function goHome() {
    if (isTrustedAbsoluteUrl(from)) {
      window.location.href = from;
    } else {
      navigate(from, { replace: true });
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const data = await signInPortal(email, password);
      if (data?.requiresOtp) {
        setOtpChallenge(data);
        toast.success(`Verification code sent to ${data.to}`);
      } else {
        toast.success('Welcome back');
        goHome();
      }
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  if (otpChallenge) {
    return (
      <div className="auth-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
          <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
        </div>
        <LoginOtpStep
          challenge={otpChallenge}
          onSuccess={() => { toast.success('Welcome back'); goHome(); }}
          onBack={() => { setOtpChallenge(null); setErr(''); }}
          onResend={async () => {
            const data = await signInPortal(email, password);
            if (data?.requiresOtp) setOtpChallenge(data);
          }}
        />
      </div>
    );
  }

  const showBookmarkHint = params.get('welcome') === '1';

  return (
    <div className="auth-card">
      <BookmarkHint show={showBookmarkHint} />
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
        <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
      </div>
      <h2>Partner Login</h2>
      <div className="auth-sub">Sign in to your clinic, staff, or admin portal.</div>

      <form onSubmit={onSubmit}>
        <FormInput label="Email" type="email" autoComplete="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@clinic.com" required />
        <FormInput label="Password" type="password" autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••" required />

        <div className="row between" style={{ marginBottom: 'var(--s-4)' }}>
          <Link to="/forgot-password" className="text-sm">Forgot password?</Link>
        </div>

        {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? <span className="spinner" /> : <LogIn size={16} />}
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="divider" />
      <div className="text-sm muted" style={{ textAlign: 'center' }}>
        New clinic? <Link to="/clinic-signup">Apply to onboard</Link>
      </div>
    </div>
  );
}

export default PortalLogin;
